import { useState, useCallback, useEffect } from "react";
import { supabase, getAuthHeader } from "@/integrations/supabase/client";
import { DEFAULT_VIDEO_MODEL } from "@/lib/aiModels";
import * as Sentry from "@sentry/react";

/**
 * Video generation for an image, shared by the master-visual lightbox and the
 * deliverable (format variant) lightbox.
 *
 * Both store the same shape in a `videos` jsonb column — on `visuals` for
 * masters, `deliverables` for format variants — so the only difference is
 * which table/row we read and write.
 *
 * An image can hold several versions; closing playback never deletes one.
 */

export interface VideoVersion {
  id: string;
  path: string | null;                 // storage path in the `renders` bucket
  prompt: string | null;               // motion prompt used to generate it
  model?: string;                      // Veo model that produced it
  createdAt: string;
  cropPercent: { widthPct: number; heightPct: number } | null;
  url?: string;                        // derived on load, not persisted
}

interface UseVideoVersionsArgs {
  /** Which table owns the row — masters live on `visuals`. */
  table: "visuals" | "deliverables";
  /** Row id; when absent the hook stays inert (nothing to persist against). */
  rowId?: string;
  /** Only load once the containing dialog is actually open. */
  isOpen: boolean;
  /** Source still image the video is generated from. */
  imageUrl: string;
  workspaceId?: string;
  userId?: string;
  formatRatio?: string;
  formatWidth?: number;
  formatHeight?: number;
}

export function useVideoVersions({
  table,
  rowId,
  isOpen,
  imageUrl,
  workspaceId,
  userId,
  formatRatio,
  formatWidth,
  formatHeight,
}: UseVideoVersionsArgs) {
  const [videos, setVideos] = useState<VideoVersion[]>([]);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showVideo, setShowVideo] = useState(false); // playback only — never deletion
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptText, setPromptText] = useState("");
  const [model, setModel] = useState<string>(DEFAULT_VIDEO_MODEL);
  const [error, setError] = useState<string | null>(null);
  // Veo always bakes in audio and offers no way to disable it, so mute by
  // default — also what browsers require for autoplay.
  const [isMuted, setIsMuted] = useState(true);
  const [recentPrompts, setRecentPrompts] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("recentVideoPrompts") || "[]").slice(0, 4);
    } catch {
      return [];
    }
  });

  const activeVideo = videos.find((v) => v.id === activeVideoId) || null;
  const isPlaying = showVideo && !!activeVideo?.url;
  const hasVideos = videos.length > 0;

  const pathToUrl = useCallback((path: string) => {
    const { data } = supabase.storage.from("renders").getPublicUrl(path);
    return data.publicUrl + "?t=" + Date.now();
  }, []);

  const persist = useCallback(async (next: VideoVersion[]) => {
    if (!rowId) return;
    const serialisable = next.map(({ url, ...rest }) => rest);
    await supabase
      .from(table as any)
      .update({ videos: serialisable as any } as any)
      .eq("id", rowId);
  }, [table, rowId]);

  // Load existing versions when the dialog opens, or when the row changes
  // (switching image versions / paging through the lightbox).
  //
  // State is reset unconditionally first: without that, moving to a row with
  // no videos would leave the previous row's versions in state, showing the
  // wrong videos AND persisting them onto the new row on the next write.
  useEffect(() => {
    setVideos([]);
    setActiveVideoId(null);
    setShowVideo(false);

    if (!isOpen || !rowId) return;
    let cancelled = false;
    (async () => {
      const { data, error: loadErr } = await supabase
        .from(table as any)
        .select("videos")
        .eq("id", rowId)
        .single();
      if (cancelled || loadErr || !data) return;
      const stored = ((data as any).videos as VideoVersion[]) || [];
      if (!Array.isArray(stored) || stored.length === 0) return;
      const withUrls = stored.map((v) => ({
        ...v,
        url: v.path ? pathToUrl(v.path) : undefined,
      }));
      setVideos(withUrls);
      setActiveVideoId(withUrls[withUrls.length - 1].id); // newest by default
    })();
    return () => { cancelled = true; };
  }, [isOpen, rowId, table, pathToUrl]);

  const generate = useCallback(async (userPrompt?: string) => {
    if (isGenerating) return;
    setShowPrompt(false);
    setError(null);

    const trimmed = userPrompt?.trim();
    if (trimmed) {
      const updated = [trimmed, ...recentPrompts.filter((p) => p !== trimmed)].slice(0, 4);
      setRecentPrompts(updated);
      localStorage.setItem("recentVideoPrompts", JSON.stringify(updated));
    }

    setIsGenerating(true);
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-video`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: authHeader },
          body: JSON.stringify({
            imageUrl,
            aspectRatio: formatRatio || "16:9",
            targetWidth: formatWidth || 1920,
            targetHeight: formatHeight || 1080,
            userPrompt: trimmed || undefined,
            workspace_id: workspaceId,
            videoModel: model,
          }),
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Video generation failed");
      }

      const data = await response.json();
      if (!data.video) throw new Error("No video returned");

      const videoId = crypto.randomUUID();
      // Each version gets a distinct object so versions never overwrite.
      let url: string | undefined = data.video;
      let path: string | null = null;
      if (rowId && userId && workspaceId) {
        const base64Part = data.video.split(",")[1];
        const byteArray = Uint8Array.from(atob(base64Part), (c) => c.charCodeAt(0));
        const blob = new Blob([byteArray], { type: "video/mp4" });
        path = `${userId}/videos/${rowId}/${videoId}.mp4`;
        const { error: uploadErr } = await supabase.storage
          .from("renders")
          .upload(path, blob, { contentType: "video/mp4", upsert: true });
        if (uploadErr) {
          console.error("Video upload error:", uploadErr);
          Sentry.captureException(uploadErr);
          path = null; // keep the inline data URL for this session
        } else {
          url = pathToUrl(path);
        }
      }

      const next = [...videos, {
        id: videoId,
        path,
        prompt: trimmed || null,
        model,
        createdAt: new Date().toISOString(),
        cropPercent: data.cropPercent || null,
        url,
      }];
      setVideos(next);
      setActiveVideoId(videoId);
      setShowVideo(true);
      await persist(next);
    } catch (err: any) {
      console.error("Video generation error:", err);
      Sentry.captureException(err);
      setError(err?.message || "Video generation failed");
    } finally {
      setIsGenerating(false);
    }
  }, [
    isGenerating, recentPrompts, imageUrl, formatRatio, formatWidth, formatHeight,
    workspaceId, userId, rowId, model, videos, persist, pathToUrl,
  ]);

  /** Stop playback — the video stays attached to the image. */
  const closePlayback = useCallback(() => setShowVideo(false), []);

  const play = useCallback((videoId: string) => {
    setActiveVideoId(videoId);
    setShowVideo(true);
  }, []);

  /** Permanently remove one version (storage object + array entry). */
  const deleteVersion = useCallback(async (videoId: string) => {
    const target = videos.find((v) => v.id === videoId);
    const next = videos.filter((v) => v.id !== videoId);
    setVideos(next);
    if (activeVideoId === videoId) {
      const fallback = next.length > 0 ? next[next.length - 1].id : null;
      setActiveVideoId(fallback);
      if (!fallback) setShowVideo(false);
    }
    try {
      if (target?.path) await supabase.storage.from("renders").remove([target.path]);
      await persist(next);
    } catch (err) {
      console.error("Error deleting video version:", err);
      Sentry.captureException(err);
    }
  }, [videos, activeVideoId, persist]);

  return {
    videos, activeVideo, activeVideoId, hasVideos, isPlaying,
    isGenerating, error,
    showPrompt, setShowPrompt,
    promptText, setPromptText,
    recentPrompts,
    model, setModel,
    isMuted, setIsMuted,
    generate, play, closePlayback, deleteVersion,
  };
}
